/**
 * Custom Models API Schemas
 *
 * CRUD for customer-provided AI models with custom endpoints.
 */

import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { ModelControlsSchema } from "../../../../models/model-controls";
import { PROTOCOL_TO_PROVIDER } from "../../../../models/protocol-to-provider";
import {
  ANTHROPIC_THINKING_DIALECTS,
  AnthropicThinkingDialectSchema
} from "../../../../models/thinking-dialect.registry";
import { ModelConfigWriteSchema } from "../../../../schemas/ModelConfig/model-config.schema";
import { safeExternalUrlSchema } from "../../../../shared/network/safe-external-url.schema";
import { ApiSuccessSchema } from "../../../common/response.schema";

/**
 * Protocols a custom model endpoint may speak. Decides which provider serves inference.
 *
 * **This is the only such list, and it is no longer a list — it is the `CustomModel.protocol`
 * COLUMN.** `DbEnum.CUSTOM_MODEL_PROTOCOL_VALUES` is generated from the `CustomModelProtocol`
 * enum in `schema.prisma`, so the vocabulary has exactly one author and Postgres refuses a
 * fourth value at the storage layer rather than accepting it and hoping.
 *
 * It used to be a hand-written tuple against a `String` column carrying the three values in a
 * comment. A second list — `SUPPORTED_PROTOCOLS`, the subset the write boundary accepted —
 * used to sit beside it, and it was the hazard rather than the guard: widening it was a
 * one-line edit in this package that silently enabled a route no inference surface
 * implemented. A customer's Anthropic model would then have reached the Anthropic chat
 * assistant, which read no credential and spent the PLATFORM key.
 *
 * Adding a member to the enum is a COMPILE ERROR in two places that must both be answered:
 * {@link PROTOCOL_TO_PROVIDER} below, and
 * `MessageProcessorService.byomAssistants` (`Record<CustomModelProtocol,
 * ByomCapableChatAssistantService>`), which admits only an assistant that has declared it
 * puts the customer's credential on the wire.
 *
 * Support is per-SURFACE and stated, never approximated: chat/agents serve all three;
 * `AiTaskExecutor` serves `openai` and REFUSES the rest by name, because its providers are
 * singletons that cache their client. See `OpenAIProviderConfig.customModel`.
 */
export const CUSTOM_MODEL_PROTOCOLS = DbEnum.CUSTOM_MODEL_PROTOCOL_VALUES;
export type CustomModelProtocol = (typeof CUSTOM_MODEL_PROTOCOLS)[number];

/**
 * The thinking dialects a customer may declare for their own endpoint, re-exported here so
 * a consumer of this contract — the picker that renders the choice — reads one list.
 *
 * Narrower than the column, which is a `ThinkingDialect` and holds all six. The two
 * Anthropic dialects are the only ones any inference surface reads off a CUSTOM endpoint
 * today: the Anthropic request builder is the one place a custom endpoint's dialect and
 * ceiling are consulted, so offering an OpenAI or Gemini dialect here would advertise a
 * setting nothing reads.
 *
 * Derived in `thinking-dialect.registry.ts` from the spec table's own `provider` field and
 * guarded there by an exhaustiveness assertion, so this is a VIEW of that registry rather
 * than a second vocabulary — the mistake `SUPPORTED_PROTOCOLS` made above.
 */
export { ANTHROPIC_THINKING_DIALECTS };

/**
 * What a custom endpoint declares about itself, shared by the read shape and both write
 * shapes so the three cannot drift.
 *
 * `null` means UNDECLARED on both fields and is a stated behaviour, not a gap — no thinking
 * block goes on the wire, and the request carries the highest ceiling the vendor grants. A
 * customer who declared the wrong dialect returns to UNDECLARED by sending `null`, which is
 * why neither field is merely optional on the update shape.
 */
const thinkingDialectField = AnthropicThinkingDialectSchema.nullable().describe(
  "Which Anthropic thinking dialect this endpoint speaks. null = undeclared: no thinking " +
    "block is sent, whatever effort level an agent configures. Never inferred — the two " +
    "dialects reject each other on the wire."
);

/**
 * Bounded at 1,000,000 — an order of magnitude above the 128,000 Anthropic currently
 * grants on its largest model, so a genuinely larger future endpoint is expressible, and
 * still far inside PostgreSQL's `INTEGER`, so a fat-fingered value is refused at the
 * contract instead of at the driver.
 */
const maxOutputTokensField = z
  .number()
  .int()
  .min(1)
  .max(1_000_000)
  .nullable()
  .describe(
    "Largest max_tokens this endpoint accepts. null = undeclared: the request carries the " +
      "highest ceiling the vendor grants, and an endpoint that cannot serve it refuses " +
      "with the exact number to put here."
  );

export { PROTOCOL_TO_PROVIDER };

/**
 * Custom model response schema — what the frontend receives
 *
 * Note: apiKey is NEVER returned. The backend stores it encrypted
 * and only uses it at inference time.
 */
export const CustomModelSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  modelName: z.string(),
  baseUrl: z.string().url(),
  protocol: z.enum(CUSTOM_MODEL_PROTOCOLS),
  enabled: z.boolean(),
  thinkingDialect: thinkingDialectField,
  maxOutputTokens: maxOutputTokensField,
  controls: ModelControlsSchema.describe(
    "What a picker may offer for this custom model on the agent surface: the effort levels, " +
      "whether a temperature slider appears and whether reasoning display is a choice. Derived " +
      "from the endpoint's protocol and declared thinking dialect. offFormDeclared is always " +
      "false here: an endpoint declaration never states how its model stops thinking, so an " +
      "absent None says nothing about whether the model always thinks."
  ),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export type CustomModel = z.infer<typeof CustomModelSchema>;

/**
 * Create custom model body
 */
export const CreateCustomModelBodySchema = z.object({
  displayName: z.string().trim().min(1).max(100),
  modelName: z.string().trim().min(1).max(200),
  baseUrl: safeExternalUrlSchema,
  protocol: z
    .enum(CUSTOM_MODEL_PROTOCOLS)
    .default("openai")
    .describe(
      "Wire protocol the endpoint speaks. Agents serve all three; AI tasks currently serve 'openai' only."
    ),
  apiKey: z.string().min(1).max(10000),
  thinkingDialect: thinkingDialectField.optional(),
  maxOutputTokens: maxOutputTokensField.optional()
});

export type CreateCustomModelBody = z.infer<typeof CreateCustomModelBodySchema>;

/**
 * Update custom model body — all fields optional
 */
export const UpdateCustomModelBodySchema = z.object({
  displayName: z.string().trim().min(1).max(100).optional(),
  modelName: z.string().trim().min(1).max(200).optional(),
  baseUrl: safeExternalUrlSchema.optional(),
  protocol: z.enum(CUSTOM_MODEL_PROTOCOLS).optional(),
  apiKey: z.string().min(1).max(10000).optional(),
  enabled: z.boolean().optional(),
  // `.nullable().optional()` and not `.optional()`: absent leaves the declaration alone,
  // explicit null CLEARS it back to undeclared. Collapsing the two would make a wrong
  // declaration unrecoverable except by deleting the endpoint.
  thinkingDialect: thinkingDialectField.optional(),
  maxOutputTokens: maxOutputTokensField.optional()
});

export type UpdateCustomModelBody = z.infer<typeof UpdateCustomModelBodySchema>;

/**
 * Path variables for single-model operations
 */
export const CustomModelPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Replace-and-delete: batch replace all references then delete the model
 */
export const ReplaceAndDeleteBodySchema = z.object({
  replacementModelConfig: ModelConfigWriteSchema
});

export type ReplaceAndDeleteBody = z.infer<typeof ReplaceAndDeleteBodySchema>;

/**
 * Reference count response
 */
export const CustomModelReferencesSchema = z.object({
  agentCount: z.number(),
  aiTaskCount: z.number(),
  agentTaskCount: z.number(),
  total: z.number()
});

const ServedCustomModelSchema = CustomModelSchema.extend({
  controls: ModelControlsSchema.optional().catch(undefined)
});

// Response schemas
export const CustomModelResponseSchema = ApiSuccessSchema(ServedCustomModelSchema);
export const CustomModelListResponseSchema = ApiSuccessSchema(z.array(ServedCustomModelSchema));
export const CustomModelDeleteResponseSchema = ApiSuccessSchema(z.object({ deleted: z.boolean() }));
export const CustomModelReferencesResponseSchema = ApiSuccessSchema(CustomModelReferencesSchema);
